import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Spinner,
  Badge,
  Button,
} from "react-bootstrap";
import axiosClient from "../api/axiosClient";
import { API_BASE_URL } from "../config/api";

const RefillsManagement = () => {
  const [autoFollowers, setAutoFollowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch Refill History data
  const fetchAutoFollowers = async (profileid = null) => {
    setLoading(true);
    try {
      const url = profileid 
        ? `https://api.viralgpt.com/admin/auto-followers?profileid=${profileid}`
        : "https://api.viralgpt.com/admin/refills";
      const response = await axiosClient.get(url);
      setAutoFollowers(response.data);
    } catch (error) {
      console.error("Error fetching Refill History:", error);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };

  // Load data when component mounts
  useEffect(() => {
    fetchAutoFollowers();
  }, []);

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title>Refill History</Card.Title>

          <div className="mb-3">
            <input
              type="text"
              className="form-control d-none"
              placeholder="Search by profile ID"
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (value === '') {
                  fetchAutoFollowers();
                } else {
                  fetchAutoFollowers(value);
                }
              }}
            />
          </div>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <>
              {/* Desktop view */}
              <div className="table-responsive d-none d-md-block">
                <Table hover className="mt-3">
                  <thead>
                    <tr>
						<th>User ID</th>
					  <th>Jap ID</th>
                      <th>Profile Link</th>
                      <th>Start Followers</th>
                      <th>Expected Followers</th>
                      <th>Current Followers</th>
                      <th>Followers Added</th>
					  <th>Status</th>
					  <th>Service ID</th>
					  <th>Service Name</th>
					

                      <th>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoFollowers.map((item, index) => (
                      <tr key={index}>
						<td>{item.userId}</td>
						<td>{item.japId}</td>
                        <td>
                          <a 
                            href={
                              item.platform === 'tiktok' 
                                ? `https://www.tiktok.com/@${item.username}`
                                : item.platform === 'youtube'
                                  ? `https://www.youtube.com/@${item.username}`
                                  : item.platform === 'instagram'
                                    ? `https://www.instagram.com/${item.username}`
                                    : `https://${item.platform}.com/${item.username}`
                            }
                            target="_blank" 
                            rel="noopener noreferrer"
                            title={item.username}
                            className="me-2"
                          >
                            <i className={`fas fa-link text-primary`} />
                          </a>
                          <Button
                            variant="outline-primary"
                            size="sm"
							className="d-none"
                            onClick={() => {
                              setSearchTerm(item.profileid);
                              fetchAutoFollowers(item.profileid);
                            }}
                            title="Filter by profile"
                          >
                            <i className="fas fa-search" />
                          </Button>
                        </td>
                        <td>{formatNumber(item.startFollowers)}</td>
                        <td>{formatNumber(item.expectedFollowers)}</td>
                        <td>{formatNumber(item.currentFollowers)}</td>
                       
                        <td>
                          <span className="text-success">+{formatNumber(item.followersAdded)}</span>
                        </td>
                        <td>
                          <Badge
                            bg={
                              item.jap_status === "Completed"
                                ? "success"
                                : item.jap_status === "Pending"
                                  ? "warning"
                                  : item.jap_status === "Processing"
                                    ? "info"
                                    : item.jap_status === "Partial"
                                      ? "secondary"
                                      : item.jap_status === "Canceled"
                                        ? "danger"
                                        : "dark"
                            }
                          >
                            {item.jap_status}
                          </Badge>
                        </td>
                        <td>{item.serviceid}</td>
                        <td>{item.platform}</td>
                        <td>{formatDate(item.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile view */}
              <div className="d-md-none">
                {autoFollowers.map((item, index) => (
                  <Card key={index} className="mb-3">
                    <Card.Body>
                      <div className="mb-2">
                        <span className="fw-bold">Profile ID: </span>
                        {item.profileid}
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <div>
                          <small className="text-muted">Start: </small>
                          {formatNumber(item.startFollowers)}
                        </div>
                        <div>
                          <small className="text-muted">Expected: </small>
                          {formatNumber(item.expectedFollowers)}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <div>
                          <small className="text-muted">Current: </small>
                          {formatNumber(item.currentFollowers)}
                        </div>
                        <div>
                          <small className="text-muted">Missing: </small>
                          <span className={item.missingFollowers > 0 ? 'text-danger' : 'text-success'}>
                            {formatNumber(item.missingFollowers)}
                          </span>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <div>
                          <small className="text-muted">Added: </small>
                          <Badge bg="success">+{formatNumber(item.followersAdded)}</Badge>
                        </div>
                        <div>
                          <small className="text-muted">Status: </small>
                          <Badge
                            bg={
                              item.jap_status === "Completed"
                                ? "success"
                                : item.jap_status === "Pending"
                                  ? "warning"
                                  : item.jap_status === "Processing"
                                    ? "info"
                                    : item.jap_status === "Partial"
                                      ? "secondary"
                                      : item.jap_status === "Canceled"
                                        ? "danger"
                                        : "dark"
                            }
                          >
                            {item.jap_status}
                          </Badge>
                        </div>
                      </div>

                      <div className="text-muted small">
                        {formatDate(item.timestamp)}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>

              {autoFollowers.length === 0 && (
                <div className="text-center my-4">
                  <p>No Refill History data found.</p>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RefillsManagement;	